import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { MessageSquare, Send, X, Minimize2, Maximize2, ThumbsUp, ThumbsDown, RefreshCw, Calendar, Clock, User, Mail, FileText, Phone } from 'lucide-react';

interface Message {
  id: string;
  type: 'user' | 'bot';
  text: string;
  timestamp: Date;
  feedback?: 'positive' | 'negative';
}

interface ChatbotAnalytics {
  totalMessages: number;
  userMessages: number;
  botMessages: number;
  positiveRatings: number;
  negativeRatings: number;
  commonQueries: Record<string, number>;
}

interface AvailableSlot {
  time: string;
  available: boolean;
}

const initialMessage: Message = {
  id: '1',
  type: 'bot',
  text: "Welcome! I'm here to help you learn more about our platform. You can ask me anything about our features, pricing, or how to get started. I can also help you schedule a consultation with one of our advisors.\n\nWhat would you like to know about our platform?\n\nRemember: I can help you with:\n• Platform features and capabilities\n• How specific features work\n• Pricing information\n• Free trial details\n• Getting started steps\n• Scheduling a consultation\n\nFeel free to ask your question, and I'll provide relevant information while highlighting how our free trial can help you experience these benefits directly.",
  timestamp: new Date()
};

const STORAGE_KEY = 'advisorgrow_chat_history';
const ANALYTICS_KEY = 'advisorgrow_chat_analytics';
const SESSION_ID_KEY = 'advisorgrow_chat_session';

// The calendar ID provided by the user
const CALENDAR_ID = "85890cb89d5f1778c397a9e81b6979af706f5d467281271203702ed7d4b6ab8d@group.calendar.google.com";
// The iCal feed URL for the calendar - not shown to users
const ICAL_FEED_URL = "https://calendar.google.com/calendar/ical/85890cb89d5f1778c397a9e81b6979af706f5d467281271203702ed7d4b6ab8d%40group.calendar.google.com/public/basic.ics";

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([initialMessage]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<ChatbotAnalytics>({
    totalMessages: 0,
    userMessages: 0,
    botMessages: 0,
    positiveRatings: 0,
    negativeRatings: 0,
    commonQueries: {}
  });
  const [isScheduling, setIsScheduling] = useState(false);
  const [schedulingStep, setSchedulingStep] = useState<'date' | 'time' | 'details'>('date');
  const [schedulingData, setSchedulingData] = useState({
    date: '',
    time: '',
    name: '',
    email: '',
    phone: '',
    purpose: '',
    duration: '30'
  });
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load chat history, session ID, and analytics from localStorage on component mount
  useEffect(() => {
    const savedMessages = localStorage.getItem(STORAGE_KEY);
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages).map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        setMessages(parsedMessages);
      } catch (error) {
        console.error('Error parsing saved messages:', error);
      }
    }

    const savedSessionId = localStorage.getItem(SESSION_ID_KEY);
    if (savedSessionId) {
      setSessionId(savedSessionId);
    } else {
      const newSessionId = crypto.randomUUID();
      setSessionId(newSessionId);
      localStorage.setItem(SESSION_ID_KEY, newSessionId);
    }

    const savedAnalytics = localStorage.getItem(ANALYTICS_KEY);
    if (savedAnalytics) {
      try {
        setAnalytics(JSON.parse(savedAnalytics));
      } catch (error) {
        console.error('Error parsing saved analytics:', error);
      }
    }
  }, []);

  // Save chat history to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    }
  }, [messages]);

  // Save analytics to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(analytics));
  }, [analytics]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Track common queries for analytics
  const updateQueryAnalytics = (query: string) => {
    const normalizedQuery = query.toLowerCase().trim();
    const keywords = [
      'pricing', 'cost', 'feature', 'trial', 'free', 
      'start', 'lead', 'capture', 'email', 'marketing',
      'social', 'media', 'landing', 'page', 'review',
      'schedule', 'appointment', 'calendar', 'meeting', 'consultation'
    ];
    
    let matched = false;
    for (const keyword of keywords) {
      if (normalizedQuery.includes(keyword)) {
        setAnalytics(prev => ({
          ...prev,
          commonQueries: {
            ...prev.commonQueries,
            [keyword]: (prev.commonQueries[keyword] || 0) + 1
          }
        }));
        matched = true;
      }
    }
    
    if (!matched) {
      setAnalytics(prev => ({
        ...prev,
        commonQueries: {
          ...prev.commonQueries,
          'other': (prev.commonQueries['other'] || 0) + 1
        }
      }));
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Update analytics
    setAnalytics(prev => ({
      ...prev,
      totalMessages: prev.totalMessages + 1,
      userMessages: prev.userMessages + 1
    }));

    // Track query for analytics
    updateQueryAnalytics(userMessage.text);

    // Check if the message is about scheduling
    const lowerCaseMessage = userMessage.text.toLowerCase();
    if (
      (lowerCaseMessage.includes('schedule') || 
       lowerCaseMessage.includes('appointment') || 
       lowerCaseMessage.includes('meeting') || 
       lowerCaseMessage.includes('consultation') || 
       lowerCaseMessage.includes('calendar')) &&
      !isScheduling
    ) {
      // Start scheduling flow
      setIsScheduling(true);
      setSchedulingStep('date');
      
      // Add bot response about scheduling
      const schedulingMessage: Message = {
        id: Date.now().toString(),
        type: 'bot',
        text: `I'd be happy to help you schedule a consultation with one of our advisors. Let's start by picking a date. Please select a date for your appointment:`,
        timestamp: new Date()
      };
      
      setTimeout(() => {
        setIsTyping(false);
        setMessages(prev => [...prev, schedulingMessage]);
        
        // Update analytics
        setAnalytics(prev => ({
          ...prev,
          totalMessages: prev.totalMessages + 1,
          botMessages: prev.botMessages + 1
        }));
      }, 1000);
      
      return;
    }
    
    // If we're in the scheduling flow, handle the scheduling steps
    if (isScheduling) {
      handleSchedulingInput(userMessage.text);
      return;
    }

    try {
      // Call the Supabase Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatbot`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ 
            message: userMessage.text,
            sessionId: sessionId
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      
      // Save session ID if returned
      if (data.sessionId) {
        setSessionId(data.sessionId);
        localStorage.setItem(SESSION_ID_KEY, data.sessionId);
      }
      
      // Calculate a realistic typing delay based on response length
      // Average reading speed is about 200-250 words per minute, or about 15 words per second
      const wordCount = data.response.split(/\s+/).length;
      const baseDelay = 500; // minimum delay in ms
      const typingDelay = Math.min(Math.max(wordCount * 100, baseDelay), 3000); // cap at 3 seconds
      
      // Simulate typing delay
      await new Promise(resolve => setTimeout(resolve, typingDelay));
      
      // Add bot response
      const botMessage: Message = {
        id: Date.now().toString(),
        type: 'bot',
        text: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      
      // Update analytics
      setAnalytics(prev => ({
        ...prev,
        totalMessages: prev.totalMessages + 1,
        botMessages: prev.botMessages + 1
      }));
    } catch (error) {
      console.error('Error calling chatbot API:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: Date.now().toString(),
        type: 'bot',
        text: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
      
      // Update analytics
      setAnalytics(prev => ({
        ...prev,
        totalMessages: prev.totalMessages + 1,
        botMessages: prev.botMessages + 1
      }));
    } finally {
      setIsTyping(false);
    }
  };

  const handleSchedulingInput = async (input: string) => {
    switch (schedulingStep) {
      case 'date':
        // Try to parse the date from the input
        try {
          let selectedDate: Date;
          
          // Check for relative dates
          if (input.toLowerCase().includes('today')) {
            selectedDate = new Date();
          } else if (input.toLowerCase().includes('tomorrow')) {
            selectedDate = new Date();
            selectedDate.setDate(selectedDate.getDate() + 1);
          } else if (input.toLowerCase().includes('next')) {
            // Handle "next Monday", "next week", etc.
            const dayMatch = input.match(/next\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
            if (dayMatch) {
              const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
                .indexOf(dayMatch[1].toLowerCase());
              
              selectedDate = new Date();
              const currentDay = selectedDate.getDay();
              const daysToAdd = (dayOfWeek + 7 - currentDay) % 7 || 7; // If today, go to next week
              selectedDate.setDate(selectedDate.getDate() + daysToAdd);
            } else {
              // Assume "next week" means 7 days from now
              selectedDate = new Date();
              selectedDate.setDate(selectedDate.getDate() + 7);
            }
          } else {
            // Try to parse as a date string
            selectedDate = new Date(input);
            
            // Check if the date is valid
            if (isNaN(selectedDate.getTime())) {
              throw new Error('Invalid date');
            }
          }
          
          // Format the date as YYYY-MM-DD
          const formattedDate = selectedDate.toISOString().split('T')[0];
          
          // Update scheduling data
          setSchedulingData(prev => ({
            ...prev,
            date: formattedDate
          }));
          
          // Add bot response
          const dateConfirmationMessage: Message = {
            id: Date.now().toString(),
            type: 'bot',
            text: `Great! You've selected ${selectedDate.toLocaleDateString()}. Let me check available time slots.`,
            timestamp: new Date()
          };
          
          setMessages(prev => [...prev, dateConfirmationMessage]);
          
          // Update analytics
          setAnalytics(prev => ({
            ...prev,
            totalMessages: prev.totalMessages + 1,
            botMessages: prev.botMessages + 1
          }));
          
          // Fetch available time slots
          setIsTyping(true);
          setIsLoadingSlots(true);
          
          try {
            // Call the calendar integration API
            const response = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-integration`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                },
                body: JSON.stringify({ 
                  action: 'checkAvailability',
                  date: formattedDate
                })
              }
            );
            
            if (!response.ok) {
              throw new Error(`Error: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (!data.success) {
              throw new Error(data.error || 'Failed to fetch available slots');
            }
            
            setAvailableSlots(data.availableSlots);
            
            // Add time selection message
            const timeSelectionMessage: Message = {
              id: Date.now().toString(),
              type: 'bot',
              text: `Here are the available time slots for ${selectedDate.toLocaleDateString()}. Please select a time:

${data.availableSlots.filter((slot: AvailableSlot) => slot.available).map((slot: AvailableSlot) => `• ${formatTimeForDisplay(slot.time)}`).join('\n')}

Please click on a time slot or type the time you prefer (e.g., "10:00").`,
              timestamp: new Date()
            };
            
            setTimeout(() => {
              setIsTyping(false);
              setIsLoadingSlots(false);
              setMessages(prev => [...prev, timeSelectionMessage]);
              
              // Update analytics
              setAnalytics(prev => ({
                ...prev,
                totalMessages: prev.totalMessages + 1,
                botMessages: prev.botMessages + 1
              }));
              
              // Move to time selection step
              setSchedulingStep('time');
            }, 1500);
          } catch (error) {
            console.error('Error fetching available slots:', error);
            
            // Add error message
            const errorMessage: Message = {
              id: Date.now().toString(),
              type: 'bot',
              text: "I'm sorry, I couldn't fetch the available time slots. Please try again later.",
              timestamp: new Date()
            };
            
            setIsTyping(false);
            setIsLoadingSlots(false);
            setMessages(prev => [...prev, errorMessage]);
            
            // Update analytics
            setAnalytics(prev => ({
              ...prev,
              totalMessages: prev.totalMessages + 1,
              botMessages: prev.botMessages + 1
            }));
            
            // Reset scheduling
            setIsScheduling(false);
          }
        } catch (error) {
          console.error('Error parsing date:', error);
          
          // Add error message
          const errorMessage: Message = {
            id: Date.now().toString(),
            type: 'bot',
            text: "I couldn't understand that date. Please enter a date in the format YYYY-MM-DD (e.g., 2025-06-15) or use phrases like 'today', 'tomorrow', or 'next Monday'.",
            timestamp: new Date()
          };
          
          setMessages(prev => [...prev, errorMessage]);
          
          // Update analytics
          setAnalytics(prev => ({
            ...prev,
            totalMessages: prev.totalMessages + 1,
            botMessages: prev.botMessages + 1
          }));
        }
        break;
        
      case 'time':
        // Try to parse the time from the input
        try {
          // Normalize the time input
          let timeInput = input.trim();
          
          // Check if the time is in the available slots
          const selectedSlot = availableSlots.find(slot => 
            slot.time === timeInput || 
            slot.time === `${timeInput}:00` || 
            `${slot.time}:00` === timeInput ||
            formatTimeForDisplay(slot.time).toLowerCase() === timeInput.toLowerCase()
          );
          
          if (!selectedSlot || !selectedSlot.available) {
            throw new Error('Invalid or unavailable time slot');
          }
          
          // Update scheduling data
          setSchedulingData(prev => ({
            ...prev,
            time: selectedSlot.time
          }));
          
          // Add bot response
          const timeConfirmationMessage: Message = {
            id: Date.now().toString(),
            type: 'bot',
            text: `Great! You've selected ${formatTimeForDisplay(selectedSlot.time)} on ${new Date(schedulingData.date).toLocaleDateString()}. Now I need a few more details to complete your appointment:

Please provide your:
1. Full name
2. Phone number
3. Email address
4. Brief purpose for the consultation

For example: "John Smith, (555) 123-4567, john@example.com, Retirement planning discussion"`,
            timestamp: new Date()
          };
          
          setMessages(prev => [...prev, timeConfirmationMessage]);
          
          // Update analytics
          setAnalytics(prev => ({
            ...prev,
            totalMessages: prev.totalMessages + 1,
            botMessages: prev.botMessages + 1
          }));
          
          // Move to details step
          setSchedulingStep('details');
        } catch (error) {
          console.error('Error parsing time:', error);
          
          // Add error message
          const errorMessage: Message = {
            id: Date.now().toString(),
            type: 'bot',
            text: `I couldn't understand that time or it's not available. Please select one of the available time slots:

${availableSlots.filter(slot => slot.available).map(slot => `• ${formatTimeForDisplay(slot.time)}`).join('\n')}`,
            timestamp: new Date()
          };
          
          setMessages(prev => [...prev, errorMessage]);
          
          // Update analytics
          setAnalytics(prev => ({
            ...prev,
            totalMessages: prev.totalMessages + 1,
            botMessages: prev.botMessages + 1
          }));
        }
        break;
        
      case 'details':
        // Try to parse the details from the input
        try {
          // Simple parsing logic - this could be more sophisticated
          const parts = input.split(',').map(part => part.trim());
          
          if (parts.length < 4) {
            throw new Error('Missing information');
          }
          
          const name = parts[0];
          const phone = parts[1];
          const email = parts[2];
          const purpose = parts.slice(3).join(', ');
          
          // Validate email with a simple regex
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(email)) {
            throw new Error('Invalid email format');
          }
          
          // Validate phone number (simple check)
          const phoneRegex = /^[\d\s()+\-\.]{7,20}$/;
          if (!phoneRegex.test(phone)) {
            throw new Error('Invalid phone number format');
          }
          
          // Update scheduling data
          setSchedulingData(prev => ({
            ...prev,
            name,
            phone,
            email,
            purpose
          }));
          
          // Add bot response
          const detailsConfirmationMessage: Message = {
            id: Date.now().toString(),
            type: 'bot',
            text: `Thank you! Here's a summary of your appointment:

• Date: ${new Date(schedulingData.date).toLocaleDateString()}
• Time: ${formatTimeForDisplay(schedulingData.time)}
• Name: ${name}
• Phone: ${phone}
• Email: ${email}
• Purpose: ${purpose}
• Duration: ${schedulingData.duration} minutes

I'm scheduling this appointment now...`,
            timestamp: new Date()
          };
          
          setMessages(prev => [...prev, detailsConfirmationMessage]);
          
          // Update analytics
          setAnalytics(prev => ({
            ...prev,
            totalMessages: prev.totalMessages + 1,
            botMessages: prev.botMessages + 1
          }));
          
          // Schedule the appointment
          setIsTyping(true);
          
          try {
            // Call the calendar integration API
            const response = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-integration`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                },
                body: JSON.stringify({ 
                  action: 'scheduleAppointment',
                  date: schedulingData.date,
                  time: schedulingData.time,
                  duration: schedulingData.duration,
                  name,
                  phone,
                  email,
                  purpose
                })
              }
            );
            
            if (!response.ok) {
              throw new Error(`Error: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (!data.success) {
              throw new Error(data.error || 'Failed to schedule appointment');
            }
            
            // Add success message
            const successMessage: Message = {
              id: Date.now().toString(),
              type: 'bot',
              text: `Great news! Your appointment has been scheduled successfully.

A confirmation email has been sent to ${email} with all the details and a calendar invitation.

Our advisor will be prepared to discuss ${purpose} with you. If you need to reschedule or cancel, please use the link in the confirmation email or call us at the number provided.

Is there anything else I can help you with today?`,
              timestamp: new Date()
            };
            
            setIsTyping(false);
            setMessages(prev => [...prev, successMessage]);
            
            // Update analytics
            setAnalytics(prev => ({
              ...prev,
              totalMessages: prev.totalMessages + 1,
              botMessages: prev.botMessages + 1
            }));
            
            // Reset scheduling
            setIsScheduling(false);
          } catch (error) {
            console.error('Error scheduling appointment:', error);
            
            // Add error message
            const errorMessage: Message = {
              id: Date.now().toString(),
              type: 'bot',
              text: "I'm sorry, I couldn't schedule the appointment. Please try again later.",
              timestamp: new Date()
            };
            
            setIsTyping(false);
            setMessages(prev => [...prev, errorMessage]);
            
            // Update analytics
            setAnalytics(prev => ({
              ...prev,
              totalMessages: prev.totalMessages + 1,
              botMessages: prev.botMessages + 1
            }));
            
            // Reset scheduling
            setIsScheduling(false);
          }
        } catch (error) {
          console.error('Error parsing details:', error);
          
          // Add error message
          const errorMessage: Message = {
            id: Date.now().toString(),
            type: 'bot',
            text: `I couldn't process those details correctly. Please provide your information in this format:

"Full Name, Phone Number, Email Address, Brief purpose for the consultation"

For example: "John Smith, (555) 123-4567, john@example.com, Retirement planning discussion"`,
            timestamp: new Date()
          };
          
          setMessages(prev => [...prev, errorMessage]);
          
          // Update analytics
          setAnalytics(prev => ({
            ...prev,
            totalMessages: prev.totalMessages + 1,
            botMessages: prev.botMessages + 1
          }));
        }
        break;
    }
  };

  const handleFeedback = async (messageId: string, feedbackType: 'positive' | 'negative') => {
    // Update message with feedback
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, feedback: feedbackType } 
          : msg
      )
    );

    // Update analytics
    setAnalytics(prev => ({
      ...prev,
      positiveRatings: feedbackType === 'positive' 
        ? prev.positiveRatings + 1 
        : prev.positiveRatings,
      negativeRatings: feedbackType === 'negative' 
        ? prev.negativeRatings + 1 
        : prev.negativeRatings
    }));

    // Send feedback to the API
    try {
      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatbot`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ 
            feedback: {
              messageId,
              type: feedbackType
            },
            sessionId: sessionId
          })
        }
      );
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  };

  const toggleChat = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const minimizeChat = () => {
    setIsMinimized(true);
  };

  const maximizeChat = () => {
    setIsMinimized(false);
  };

  const closeChat = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  const clearConversation = async () => {
    setMessages([initialMessage]);
    setIsScheduling(false);
    setSchedulingStep('date');
    
    // Reset conversation on the server
    try {
      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatbot`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ 
            reset: true,
            sessionId: sessionId
          })
        }
      );
      
      // Generate a new session ID
      const newSessionId = crypto.randomUUID();
      setSessionId(newSessionId);
      localStorage.setItem(SESSION_ID_KEY, newSessionId);
      
    } catch (error) {
      console.error('Error resetting conversation:', error);
    }
  };

  // Format time for display
  const formatTimeForDisplay = (time: string) => {
    // Check if time is in 24-hour format (e.g., "14:00")
    if (/^\d{1,2}:\d{2}$/.test(time)) {
      const [hours, minutes] = time.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
    return time;
  };

  return (
    <>
      {/* Chat button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="fixed bottom-6 right-6 bg-primary text-white p-4 rounded-full shadow-lg hover:bg-primary-dark transition-colors z-50"
          aria-label="Open chat"
        >
          <MessageSquare className="h-6 w-6" />
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div
          className={`fixed bottom-6 right-6 bg-white rounded-lg shadow-xl z-50 transition-all duration-300 overflow-hidden ${
            isMinimized 
              ? 'w-72 h-16' 
              : 'w-80 sm:w-96 h-[500px] max-h-[80vh]'
          }`}
        >
          {/* Chat header */}
          <div className="bg-primary text-white p-4 flex justify-between items-center">
            <div className="flex items-center">
              <MessageSquare className="h-5 w-5 mr-2" />
              <h3 className="font-medium">AdvisorGrow Assistant</h3>
            </div>
            <div className="flex items-center space-x-2">
              {isMinimized ? (
                <button onClick={maximizeChat} className="text-white hover:text-gray-200">
                  <Maximize2 className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={minimizeChat} className="text-white hover:text-gray-200">
                  <Minimize2 className="h-4 w-4" />
                </button>
              )}
              <button onClick={closeChat} className="text-white hover:text-gray-200">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Chat messages */}
              <div className="p-4 h-[calc(100%-8rem)] overflow-y-auto">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`mb-4 ${
                      message.type === 'user' ? 'text-right' : ''
                    }`}
                  >
                    <div
                      className={`inline-block max-w-[85%] rounded-lg px-4 py-2 ${
                        message.type === 'user'
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <div className="whitespace-pre-line">{message.text}</div>
                    </div>
                    <div className="flex items-center mt-1">
                      <div className="text-xs text-gray-500">
                        {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      
                      {/* Feedback buttons for bot messages */}
                      {message.type === 'bot' && message.id !== '1' && (
                        <div className="ml-2 flex space-x-1">
                          <button 
                            onClick={() => handleFeedback(message.id, 'positive')}
                            className={`p-1 rounded-full ${
                              message.feedback === 'positive' 
                                ? 'bg-green-100 text-green-600' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title="Helpful"
                          >
                            <ThumbsUp className="h-3 w-3" />
                          </button>
                          <button 
                            onClick={() => handleFeedback(message.id, 'negative')}
                            className={`p-1 rounded-full ${
                              message.feedback === 'negative' 
                                ? 'bg-red-100 text-red-600' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title="Not helpful"
                          >
                            <ThumbsDown className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="mb-4">
                    <div className="inline-block max-w-[85%] rounded-lg px-4 py-2 bg-gray-100 text-gray-800">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Calendar UI for scheduling */}
                {isScheduling && schedulingStep === 'date' && (
                  <div className="mb-4">
                    <div className="inline-block max-w-[85%] rounded-lg px-4 py-2 bg-gray-100 text-gray-800">
                      <div className="flex items-center mb-2">
                        <Calendar className="h-5 w-5 text-primary mr-2" />
                        <span className="font-medium">Select a date</span>
                      </div>
                      <input
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                        onChange={(e) => {
                          setInputValue(e.target.value);
                          if (e.target.value) {
                            handleSendMessage();
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Time slot selection UI */}
                {isScheduling && schedulingStep === 'time' && (
                  <div className="mb-4">
                    <div className="inline-block max-w-[85%] rounded-lg px-4 py-2 bg-gray-100 text-gray-800">
                      <div className="flex items-center mb-2">
                        <Clock className="h-5 w-5 text-primary mr-2" />
                        <span className="font-medium">Select a time</span>
                      </div>
                      
                      {isLoadingSlots ? (
                        <div className="flex justify-center py-4">
                          <RefreshCw className="h-5 w-5 text-primary animate-spin" />
                        </div>
                      ) : availableSlots.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2">
                          {availableSlots
                            .filter(slot => slot.available)
                            .map(slot => (
                              <button
                                key={slot.time}
                                className="p-2 border rounded-md hover:bg-primary hover:text-white transition-colors"
                                onClick={() => {
                                  setInputValue(slot.time);
                                  handleSendMessage();
                                }}
                              >
                                {formatTimeForDisplay(slot.time)}
                              </button>
                            ))
                          }
                        </div>
                      ) : (
                        <p className="text-center py-2 text-gray-500">No available time slots for this date</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Contact details form */}
                {isScheduling && schedulingStep === 'details' && (
                  <div className="mb-4">
                    <div className="inline-block max-w-[85%] rounded-lg px-4 py-2 bg-gray-100 text-gray-800">
                      <div className="flex items-center mb-2">
                        <User className="h-5 w-5 text-primary mr-2" />
                        <span className="font-medium">Your Details</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 mr-2" />
                          <input
                            type="text"
                            placeholder="Full Name *"
                            className="flex-1 p-2 border rounded-md text-sm"
                            value={schedulingData.name}
                            onChange={(e) => setSchedulingData(prev => ({
                              ...prev,
                              name: e.target.value
                            }))}
                            required
                          />
                        </div>
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 text-gray-400 mr-2" />
                          <input
                            type="tel"
                            placeholder="Phone Number *"
                            className="flex-1 p-2 border rounded-md text-sm"
                            value={schedulingData.phone}
                            onChange={(e) => setSchedulingData(prev => ({
                              ...prev,
                              phone: e.target.value
                            }))}
                            required
                          />
                        </div>
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 text-gray-400 mr-2" />
                          <input
                            type="email"
                            placeholder="Email Address *"
                            className="flex-1 p-2 border rounded-md text-sm"
                            value={schedulingData.email}
                            onChange={(e) => setSchedulingData(prev => ({
                              ...prev,
                              email: e.target.value
                            }))}
                            required
                          />
                        </div>
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 text-gray-400 mr-2" />
                          <input
                            type="text"
                            placeholder="Purpose of Meeting *"
                            className="flex-1 p-2 border rounded-md text-sm"
                            value={schedulingData.purpose}
                            onChange={(e) => setSchedulingData(prev => ({
                              ...prev,
                              purpose: e.target.value
                            }))}
                            required
                          />
                        </div>
                        <button
                          className="w-full mt-2 p-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
                          onClick={() => {
                            if (schedulingData.name && schedulingData.phone && schedulingData.email && schedulingData.purpose) {
                              setInputValue(`${schedulingData.name}, ${schedulingData.phone}, ${schedulingData.email}, ${schedulingData.purpose}`);
                              handleSendMessage();
                            } else {
                              alert("Please fill in all required fields");
                            }
                          }}
                        >
                          Schedule Appointment
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Chat input */}
              <div className="p-4 border-t">
                <div className="flex items-center">
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={isScheduling 
                      ? schedulingStep === 'date' 
                        ? "Enter a date (e.g., tomorrow, next Monday)" 
                        : schedulingStep === 'time'
                          ? "Enter a time (e.g., 10:00)"
                          : "Enter your details (Name, Phone, Email, Purpose)"
                      : "Type your message..."
                    }
                    className="flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    rows={1}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isTyping}
                    className={`ml-2 p-2 rounded-full ${
                      !inputValue.trim() || isTyping
                        ? 'bg-gray-200 text-gray-400'
                        : 'bg-primary text-white hover:bg-primary-dark'
                    }`}
                  >
                    <Send className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <button 
                    onClick={clearConversation}
                    className="hover:text-primary flex items-center"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    New conversation
                  </button>
                  <span>
                    {messages.length > 1 ? `${messages.length - 1} messages` : ''}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;